import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { KeyRound, ExternalLink } from 'lucide-react';

const APP_SCHEME = 'com.peespees.giftmap';

/**
 * Web bridge page for password reset deep linking.
 * Supabase redirects here with tokens in the URL hash.
 * This page extracts tokens and redirects to the native app via custom scheme.
 */
const UpdatePasswordBridge = () => {
  const [showManual, setShowManual] = useState(false);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [noTokens, setNoTokens] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type') || 'recovery';

    if (!accessToken || !refreshToken) {
      console.warn('UpdatePasswordBridge: no tokens found in URL hash');
      setNoTokens(true);
      return;
    }

    const link = `${APP_SCHEME}://update-password?access_token=${encodeURIComponent(accessToken)}&refresh_token=${encodeURIComponent(refreshToken)}&type=${type}`;
    setDeepLink(link);

    // Attempt auto-redirect
    window.location.href = link;

    // Show manual button after a delay in case redirect didn't work
    const timer = setTimeout(() => setShowManual(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (noTokens) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired. Please request a new one from the app.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            {showManual
              ? 'Tap the button below to open the app and set your new password.'
              : 'Opening the app...'}
          </CardDescription>
        </CardHeader>
        {showManual && deepLink && (
          <CardContent className="space-y-4">
            <Button
              className="w-full"
              onClick={() => { window.location.href = deepLink; }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in App
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Don't have the app? Download it from the App Store or Google Play.
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default UpdatePasswordBridge;
